import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api/index.js';
import { DynamicIcon } from '../components/icons.jsx';
import { ConfirmModal } from "../components/Common.jsx";
import { PERMISSIONS } from '../constants/permissions.js';
import { hasPermission } from '../utils/permissions.js';

const PlatformVariablesTab = ({ token, currentUser }) => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [deleteKey, setDeleteKey] = useState(null);
    const [newSetting, setNewSetting] = useState({ setting_key: '', setting_value: '', data_type: 'string', category: 'Custom' });

    const fetchSettings = async () => {
        try {
            const res = await apiFetch('/admin/platform-settings', token, { method: 'GET' });
            setSettings(res);
            setLoading(false);
        } catch (err) {
            setMessage('Failed to load settings');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token && currentUser?.roleId === 'r1') {
            fetchSettings();
        } else {
            setLoading(false);
        }
    }, [token, currentUser]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const payload = {
                settings: settings.filter(s => s.is_editable).map(s => ({
                    setting_key: s.setting_key,
                    setting_value: String(s.setting_value)
                }))
            };
            await apiFetch('/admin/platform-settings', token, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
            setMessage('Settings updated successfully!');
        } catch (err) {
            setMessage('Failed to update settings');
        }
        setSaving(false);
    };

    const handleChange = (key, val) => {
        setSettings(prev => prev.map(s => s.setting_key === key ? { ...s, setting_value: val } : s));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!newSetting.setting_key || !newSetting.setting_value) {
            setMessage('Key and Value are required for new settings.');
            return;
        }
        setSaving(true);
        try {
            const res = await apiFetch('/admin/platform-settings', token, {
                method: 'POST',
                body: JSON.stringify(newSetting)
            });
            setSettings([...settings, res]);
            setIsAdding(false);
            setNewSetting({ setting_key: '', setting_value: '', data_type: 'string', category: 'Custom' });
            setMessage('New variable added successfully!');
        } catch (err) {
            setMessage(err.message || 'Failed to add variable');
        }
        setSaving(false);
    };

    const executeDelete = async () => {
        if (!deleteKey) return;
        setSaving(true);
        try {
            await apiFetch(`/admin/platform-settings/${deleteKey}`, token, { method: 'DELETE' });
            setSettings(prev => prev.filter(s => s.setting_key !== deleteKey));
            setMessage(`Variable ${deleteKey} deleted successfully.`);
        } catch (err) {
            setMessage(err.message || 'Failed to delete variable');
        }
        setSaving(false);
        setDeleteKey(null);
    };

    if (currentUser?.roleId !== 'r1') {
        return <div className="p-6">Access restricted to Super Admins only.</div>;
    }

    if (loading) return <div className="p-6">Loading...</div>;

    // Group by category
    const grouped = settings.reduce((acc, curr) => {
        if (!acc[curr.category]) acc[curr.category] = [];
        acc[curr.category].push(curr);
        return acc;
    }, {});

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">System Variables</h2>
                    <p className="text-sm text-gray-500">Manage global platform configurations replacing legacy .env values.</p>
                </div>
                <div className="space-x-3">
                    <button
                        onClick={() => setIsAdding(!isAdding)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-300 rounded-md hover:bg-gray-200"
                    >
                        {isAdding ? 'Cancel' : 'Add Variable'}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {isAdding && (
                <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6 border border-indigo-100 dark:border-slate-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Variable</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <input
                            type="text"
                            placeholder="SETTING_KEY"
                            value={newSetting.setting_key}
                            onChange={(e) => setNewSetting({ ...newSetting, setting_key: e.target.value.toUpperCase() })}
                            className="px-3 py-2 border rounded-md dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        />
                        <input
                            type="text"
                            placeholder="Value"
                            value={newSetting.setting_value}
                            onChange={(e) => setNewSetting({ ...newSetting, setting_value: e.target.value })}
                            className="px-3 py-2 border rounded-md dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        />
                        <select
                            value={newSetting.data_type}
                            onChange={(e) => setNewSetting({ ...newSetting, data_type: e.target.value })}
                            className="px-3 py-2 border rounded-md dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        >
                            <option value="string">String</option>
                            <option value="int">Integer</option>
                            <option value="bool">Boolean</option>
                            <option value="json">JSON</option>
                        </select>
                        <select
                            value={newSetting.category}
                            onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value })}
                            className="px-3 py-2 border rounded-md dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        >
                            <option value="Custom">Custom</option>
                            <option value="Storage">Storage</option>
                            <option value="Compute">Compute</option>
                            <option value="Email">Email</option>
                            <option value="Queue">Queue</option>
                            <option value="Security">Security</option>
                        </select>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleAddSubmit}
                            disabled={saving}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}

            {message && (
                <div className={`p-4 rounded-md ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message}
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-8">
                {Object.keys(grouped).sort().map(category => (
                    <div key={category} className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 border-b pb-2 dark:border-slate-700">
                            {category}
                        </h3>
                        <div className="space-y-4">
                            {grouped[category].map(setting => (
                                <div key={setting.setting_key} className="flex items-center gap-4">
                                    <div className="w-1/3 text-sm font-medium text-gray-700 dark:text-gray-300 break-all">
                                        {setting.setting_key}
                                    </div>
                                    <div className="flex-1 flex gap-2 items-center">
                                        <input
                                            type={setting.data_type === 'int' ? 'number' : 'text'}
                                            disabled={!setting.is_editable}
                                            value={setting.setting_value || ''}
                                            onChange={(e) => handleChange(setting.setting_key, e.target.value)}
                                            className="flex-1 px-3 py-2 border rounded-md dark:bg-slate-900 dark:border-slate-700 dark:text-white disabled:opacity-50 disabled:bg-gray-100"
                                        />
                                        {setting.is_editable && (
                                            <button
                                                type="button"
                                                title="Delete variable"
                                                onClick={() => setDeleteKey(setting.setting_key)}
                                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition"
                                            >
                                                <DynamicIcon name="Trash2" className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </form>

            <ConfirmModal
                isOpen={!!deleteKey}
                onClose={() => setDeleteKey(null)}
                onConfirm={executeDelete}
                title="Delete Variable"
                message={`Are you sure you want to delete the variable '${deleteKey}'? This action cannot be undone.`}
                confirmText="Delete"
                isDestructive={true}
            />
        </div>
    );
};

export default PlatformVariablesTab;
